/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import * as _ from 'lodash';
import { LocalDate, Period } from '@js-joda/core';
import React, { ReactComponentElement } from 'react';

// models and services
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';

// components
import BackButton from '../../components/back-button/BackButton';
import BasicDrawer from './common/BasicDrawer';
import { Alert, ButtonPrimary, DateInput, DayInput, Label, MonthInput, YearInput } from '@americanexpress/dls-react';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// styles
import { PRIVACY_POLICY } from 'src/utils/constants';
import './AgeDrawer.scss';

export default class AgeDrawer extends BasicDrawer<
    any,
    {
        age: number;
        dob: LocalDate;
        invalidDate: boolean;
    }
> {
    public override state: any = {
        age: null as number,
        dob: null as LocalDate,
        invalidDate: false
    };

    public override render(): ReactComponentElement<any> {
        const userDob = _.get(this, 'props.user.dob');
        const dob = this.state.dob ? this.state.dob : userDob || LocalDate.now().minusYears(30);

        return (
            <div className="age-drawer drawer">
                <BackButton drawerProps={this.props.drawerProps} />
                <Typography variant={TypographyVariants.HEADING_S} className="after24 textStrong">
                    Adjust your age
                </Typography>

                <Typography variant={TypographyVariants.SUBTEXT} className="description textDefault after24">
                    Your timeline and projections are centered around your age. {LiteralsService.getAppName()} works for
                    ages 18–99.
                </Typography>

                <Label htmlFor="date-input-1">Change your birthdate</Label>
                <DateInput
                    id="date-input-1"
                    maxDate={FormatService.formatSqlDate(LocalDate.now().minusYears(13))}
                    minDate={FormatService.formatSqlDate(LocalDate.now().minusYears(100))}
                    value={FormatService.formatSqlDate(dob)}
                    onChange={(date: any) => {
                        this.setBirthday(date);
                    }}
                >
                    <MonthInput id="default-month" />
                    <DayInput id="default-day" />
                    <YearInput id="default-year" />
                </DateInput>
                <Alert id="dateInputAlert" show={false}>
                    Enter a date of birth.
                </Alert>
                <Alert id="rangeAlert" show={false}>
                    Enter a valid date of birth.
                </Alert>
                <Alert id="labelAlert" show={false}>
                    Enter undefined and undefined.
                </Alert>
                <Alert id="monthAlert" show={false}>
                    Select a month.
                </Alert>
                <Alert id="dayAlert" show={false}>
                    Select a day.
                </Alert>
                <Alert id="yearAlert" show={false}>
                    Enter a valid a year.
                </Alert>

                <Typography variant={TypographyVariants.SUBTEXT} className="privacy before24 after24">
                    We care about your privacy.{' '}
                    <a href={PRIVACY_POLICY} target="_blank" rel="noopener noreferrer">
                        Read our Privacy Policy
                    </a>
                    .
                </Typography>

                <div key="buttons" className="grid-tile">
                    <ButtonPrimary
                        disabled={this.state.invalidDate}
                        className="button"
                        onClick={() => {
                            if (this.state.invalidDate) return;
                            this.handleSave();
                        }}
                    >
                        Update age
                    </ButtonPrimary>
                </div>
            </div>
        );
    }

    private setBirthday(dob: any): void {
        if (dob.date) {
            const localDob: LocalDate = LocalDate.parse(dob.date);
            const age: number = Period.between(localDob, LocalDate.now()).years();

            this.setState({
                age,
                dob: localDob,
                invalidDate: false
            });
        } else {
            this.setState({
                invalidDate: true
            });
        }
    }

    private handleSave(): void {
        this.props.handleChangeUser(this.state.dob, 'dob');
        this.props.handleBack();
    }
}
